import { useState } from "react";
import type { CaseSegment } from "../../hooks/useCaseActionBoard";
import { type BoardSeat, useUpsertSeat } from "../../hooks/useBoardSeats";
import { isLive, type ClaimSummaryRow } from "../../hooks/useClaimsSummary";

/** The showdown board (O.K. Corral) over the saloon-street backdrop. Parties are
 *  translucent gunfighters at preset spots; the plaintiff fires a volley of
 *  rounds (= live claims) that thins as claims are defeated. */
const W = 1000;
const H = 560;
// Preset spots on the street, each with a short scene label.
const SEATS: Record<string, { x: number; y: number; tag: string }> = {
  top_left: { x: 244, y: 322, tag: "saloon side" },
  top: { x: 482, y: 300, tag: "up the street" },
  top_right: { x: 700, y: 314, tag: "store side" },
  left: { x: 150, y: 398, tag: "left porch" },
  center: { x: 500, y: 388, tag: "mid-street" },
  right: { x: 856, y: 398, tag: "right walk" },
  bottom_left: { x: 198, y: 470, tag: "near-left" },
  bottom: { x: 500, y: 484, tag: "foreground" },
  bottom_right: { x: 818, y: 452, tag: "the barrels" },
};
const SEAT_KEYS = Object.keys(SEATS);

function anchorFor(seat: BoardSeat): { x: number; y: number } {
  if (seat.pos_x != null && seat.pos_y != null) return { x: seat.pos_x * W, y: seat.pos_y * H };
  return SEATS[seat.seat] ?? SEATS.left!;
}

/** A translucent gunfighter silhouette, feet at (x,y). */
function Gunman({ x, y, flip, fill, opacity, scale = 1 }: { x: number; y: number; flip?: boolean; fill: string; opacity: number; scale?: number }) {
  return (
    <g transform={`translate(${x},${y}) scale(${(flip ? -1 : 1) * scale},${scale})`} style={{ fill, fillOpacity: opacity }}>
      <ellipse cx="0" cy="-46" rx="13" ry="3" />
      <path d="M-7,-46 Q0,-59 7,-46 Z" />
      <circle cx="0" cy="-42" r="4.5" />
      <path d="M-8,-38 L8,-38 L6,-12 L-6,-12 Z" />
      <path d="M-6,-12 L-1,-12 L-2,0 L-7,0 Z" />
      <path d="M1,-12 L6,-12 L8,0 L3,0 Z" />
      <path d="M7,-34 L23,-30 L23,-27 L7,-31 Z" />
      <rect x="21" y="-32" width="9" height="4" rx="1" />
    </g>
  );
}

interface Props {
  caseId: string;
  segments: CaseSegment[];
  seats: BoardSeat[];
  live: number;
  total: number;
  /** Counterclaims raised against the plaintiff — drives the return volley. */
  counterclaims?: ClaimSummaryRow[];
  selectedSeatId: string | null;
  rearrange: boolean;
  onSelect: (partyId: string | null, seatId: string) => void;
}

function BoardCanvas({ caseId, segments, seats, live, total, counterclaims = [], selectedSeatId, rearrange, onSelect }: Props) {
  const upsert = useUpsertSeat(caseId);
  const [moveSel, setMoveSel] = useState<string | null>(null); // seat id picked up for moving

  const seatCounts: Record<string, number> = {};
  seats.forEach((s) => { seatCounts[s.seat] = (seatCounts[s.seat] ?? 0) + 1; });
  const seatIdx: Record<string, number> = {};
  const placed = seats.map((s) => {
    const idx = (seatIdx[s.seat] = (seatIdx[s.seat] ?? 0) + 1) - 1;
    const cnt = seatCounts[s.seat] ?? 1;
    const a = anchorFor(s);
    return { seat: s, p: { x: a.x + (cnt > 1 ? (idx - (cnt - 1) / 2) * 70 : 0), y: a.y } };
  });

  const plaintiffs = placed.filter((q) => q.seat.kind === "plaintiff");
  const defendants = placed.filter((q) => q.seat.kind === "defendant");
  const width = Math.max(2, 3 + live * 1.6);

  // return volley — live counterclaims fired back at the plaintiff, by asserting party
  const ccLiveByParty: Record<string, number> = {};
  counterclaims.forEach((c) => {
    if (c.asserting_party_id && isLive(c.survival)) {
      ccLiveByParty[c.asserting_party_id] = (ccLiveByParty[c.asserting_party_id] ?? 0) + 1;
    }
  });

  const movingLabel = moveSel ? seats.find((s) => s.id === moveSel)?.label : null;

  function placeAt(seatKey: string) {
    if (!moveSel) return;
    upsert.mutate({ id: moveSel, seat: seatKey, pos_x: null, pos_y: null });
    setMoveSel(null);
  }
  function tapToken(seatId: string, partyId: string | null) {
    if (rearrange) setMoveSel(moveSel === seatId ? null : seatId);
    else onSelect(partyId, seatId);
  }

  function hasAction(partyId: string | null): boolean {
    if (!partyId) return false;
    return segments.some((g) => g.party_id === partyId && (g.status === "open" || g.status === "acted")
      && g.owner === "responding_party" && g.deadline_date != null);
  }

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="100%" preserveAspectRatio="xMidYMid meet"
      role="img" style={{ display: "block" }}>
      <title>The showdown — the plaintiff fires a volley of claims at the defendants</title>

      {/* ===== THE VOLLEY ===== */}
      {plaintiffs.flatMap((p) => defendants.map((d) => {
        const a = p.p, b = d.p;
        const dx = b.x - a.x, dy = b.y - a.y, len = Math.hypot(dx, dy) || 1;
        const ux = dx / len, uy = dy / len;
        const sx = a.x + ux * 34, sy = a.y - 30 + uy * 34;
        const ex = b.x - ux * 30, ey = b.y - 26;
        return (
          <g key={`${p.seat.id}-${d.seat.id}`}>
            <line x1={sx} y1={sy} x2={ex} y2={ey}
              style={{ stroke: "rgb(var(--c-ember-bright))", strokeWidth: width, strokeLinecap: "round", opacity: 0.3 }} />
            <line x1={sx} y1={sy} x2={ex} y2={ey}
              style={{ stroke: "rgb(var(--c-ember-hot))", strokeWidth: 2.2, strokeLinecap: "round", strokeDasharray: "2 13" }} />
            <g transform={`translate(${sx},${sy}) rotate(${(Math.atan2(uy, ux) * 180) / Math.PI})`} style={{ fill: "rgb(var(--c-ember-hot))" }}>
              <path d="M0,0 L20,-5 L6,0 L20,6 Z" />
              <path d="M0,0 L12,-12 L4,-4 L15,8 Z" />
              <circle cx="0" cy="0" r="4.5" style={{ fill: "rgb(var(--c-bone))" }} />
            </g>
          </g>
        );
      }))}

      {/* ===== THE RETURN VOLLEY (counterclaims) ===== */}
      {defendants.flatMap((d) => {
        const n = d.seat.party_id ? ccLiveByParty[d.seat.party_id] ?? 0 : 0;
        if (n <= 0) return [];
        const cw = Math.max(2, 2 + n * 1.5);
        return plaintiffs.map((p) => {
          const a = d.p, b = p.p;
          const dx = b.x - a.x, dy = b.y - a.y, len = Math.hypot(dx, dy) || 1;
          const ux = dx / len, uy = dy / len;
          const sx = a.x + ux * 30, sy = a.y - 28 + uy * 30;
          const ex = b.x - ux * 30, ey = b.y - 26;
          return (
            <g key={`cc-${d.seat.id}-${p.seat.id}`}>
              <line x1={sx} y1={sy} x2={ex} y2={ey}
                style={{ stroke: "rgb(var(--c-blue-bright))", strokeWidth: cw, strokeLinecap: "round", opacity: 0.26 }} />
              <line x1={sx} y1={sy} x2={ex} y2={ey}
                style={{ stroke: "rgb(var(--c-blue-bright))", strokeWidth: 2, strokeLinecap: "round", strokeDasharray: "2 13" }} />
              <g transform={`translate(${sx},${sy}) rotate(${(Math.atan2(uy, ux) * 180) / Math.PI})`} style={{ fill: "rgb(var(--c-blue-bright))" }}>
                <path d="M0,0 L18,-4 L6,0 L18,5 Z" />
                <circle cx="0" cy="0" r="4" style={{ fill: "rgb(var(--c-bone))" }} />
              </g>
            </g>
          );
        });
      })}

      {/* round tally */}
      {plaintiffs[0] && (() => {
        const a = plaintiffs[0]!.p;
        const ticks = Math.min(total, 14);
        return (
          <g>
            <rect x={a.x - 70} y={a.y + 66} width={140} height={36} rx={7}
              style={{ fill: "rgb(var(--c-iron-2))", fillOpacity: 0.78, stroke: "rgb(var(--c-ember-bright))" }} />
            <text x={a.x} y={a.y + 82} textAnchor="middle" style={{ fill: "rgb(var(--c-bone))", font: "600 12px Oswald, sans-serif", letterSpacing: 0.5 }}>
              {live} / {total} ROUNDS
            </text>
            {Array.from({ length: ticks }).map((_, i) => (
              <line key={i} x1={a.x - 60 + i * 9} y1={a.y + 90} x2={a.x - 60 + i * 9} y2={a.y + 98}
                style={{ stroke: i < live ? "rgb(var(--c-ember-hot))" : "rgb(var(--c-steel))", strokeWidth: 2 }} />
            ))}
          </g>
        );
      })()}

      {/* ===== preset spots (rearrange mode) ===== */}
      {rearrange && SEAT_KEYS.map((k) => {
        const s = SEATS[k]!;
        return (
          <g key={`slot-${k}`} style={{ cursor: moveSel ? "pointer" : "default" }} onClick={() => placeAt(k)}>
            <circle cx={s.x} cy={s.y - 20} r={26}
              style={{ fill: "rgb(var(--c-bone) / 0.04)", stroke: "rgb(var(--c-bone-mute))", strokeOpacity: moveSel ? 0.9 : 0.4, strokeWidth: 1.5, strokeDasharray: "4 4" }} />
            <text x={s.x} y={s.y + 16} textAnchor="middle" style={{ fill: "rgb(var(--c-bone-mute))", font: "400 8px Oswald, sans-serif", opacity: 0.7 }}>{s.tag}</text>
          </g>
        );
      })}
      {rearrange && movingLabel && (
        <text x={W / 2} y={26} textAnchor="middle" style={{ fill: "rgb(var(--c-ember-hot))", font: "500 13px Oswald, sans-serif" }}>
          tap a spot to place {movingLabel}
        </text>
      )}

      {/* ===== GUNFIGHTERS ===== */}
      {placed.map((q) => {
        const s = q.seat;
        const a = q.p;
        const isSel = s.id === selectedSeatId;
        const picked = moveSel === s.id;
        const aggressor = s.kind === "plaintiff";
        const outside = s.kind === "outside";
        const faceLeft = a.x > W / 2;
        const fill = aggressor ? "rgb(var(--c-ember-bright))" : outside ? "rgb(var(--c-bone-mute))" : "rgb(var(--c-bone))";
        const plateStroke = picked ? "rgb(var(--c-ember-hot))" : isSel ? "rgb(var(--c-ember-bright))"
          : aggressor ? "rgb(var(--c-ember))" : outside ? "rgb(var(--c-steel-dark))" : "rgb(var(--c-steel))";
        return (
          <g key={s.id} style={{ cursor: "pointer" }} onClick={() => tapToken(s.id, s.party_id)}>
            <Gunman x={a.x} y={a.y - 4} flip={faceLeft} fill={fill}
              opacity={picked ? 0.95 : aggressor ? 0.7 : 0.55} scale={aggressor ? 1.12 : outside ? 0.82 : 1} />
            {hasAction(s.party_id) && (
              <circle cx={a.x + (faceLeft ? -18 : 18)} cy={a.y - 50} r={5} style={{ fill: "rgb(var(--c-ember-hot))" }} />
            )}
            <rect x={a.x - 82} y={a.y + 6} width={164} height={isSel || picked ? 38 : 36} rx={6}
              style={{ fill: "rgb(var(--c-iron-2))", fillOpacity: 0.72, stroke: plateStroke, strokeWidth: isSel || picked ? 2.5 : 1.5, strokeDasharray: outside ? "5 3" : undefined }} />
            <text x={a.x} y={a.y + 25} textAnchor="middle" style={{ fill: "rgb(var(--c-bone))", font: "500 12px Oswald, sans-serif" }}>
              {s.label.slice(0, 24)}
            </text>
            <text x={a.x} y={a.y + 38} textAnchor="middle" style={{ fill: "rgb(var(--c-bone-mute))", font: "400 9px Oswald, sans-serif" }}>
              {s.group_label ? s.group_label.slice(0, 32) : aggressor ? "drew first" : outside ? "bystander" : s.kind}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export default BoardCanvas;
